// =============================================
// UTILITÁRIOS DE VALIDAÇÃO DE USUÁRIO
// =============================================

import { supabase } from '../lib/supabase'
import { normalizeEmail, normalizePersonName } from './normalizeUserFields'

export { normalizeEmail, normalizePersonName }

// Regex para validação de email
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Interface para resultado de validação
export interface ValidationResult {
  isValid: boolean
  error?: string
}

// Interface para resultado de verificação de usuário
export interface UserCheckResult {
  exists: boolean
  error?: string
  userInfo?: any
}

export interface ExistingUserInfo {
  id: string
  email?: string
  created_at?: string
  email_confirmed_at?: string | null
  profile_name?: string | null
  profile_active?: boolean | null
}

export interface SignUpResult {
  success: boolean
  message: string
  userId?: string
  isDuplicate?: boolean
}

interface SignUpResponseData {
  user?: { id: string; identities?: { id: string }[] } | null
  session?: { user?: { id: string; identities?: { id: string }[] } } | null
}

/**
 * Valida formato de email
 */
export const validateEmailFormat = (email: string): ValidationResult => {
  if (!email || !email.trim()) {
    return {
      isValid: false,
      error: 'E-mail é obrigatório'
    }
  }

  const normalizedEmail = normalizeEmail(email)
  
  if (!EMAIL_REGEX.test(normalizedEmail)) {
    return {
      isValid: false,
      error: 'E-mail deve ter um formato válido (exemplo@dominio.com)'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Valida senha
 */
export const validatePassword = (password: string, confirmPassword?: string): ValidationResult => {
  if (!password) {
    return {
      isValid: false,
      error: 'Senha é obrigatória'
    }
  }

  if (password.length < 6) {
    return {
      isValid: false,
      error: 'A senha deve ter pelo menos 6 caracteres'
    }
  }

  if (confirmPassword !== undefined && password !== confirmPassword) {
    return {
      isValid: false,
      error: 'As senhas não coincidem'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Valida nome
 */
export const validateName = (name: string): ValidationResult => {
  if (!name || !name.trim()) {
    return {
      isValid: false,
      error: 'Nome é obrigatório'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Verifica se um usuário já existe no sistema
 */
export const checkUserExists = async (email: string): Promise<UserCheckResult> => {
  try {
    const normalizedEmail = normalizeEmail(email)

    // Primeiro valida o formato do email
    const emailValidation = validateEmailFormat(normalizedEmail)
    if (!emailValidation.isValid) {
      return {
        exists: false,
        error: emailValidation.error
      }
    }

    // Tenta usar a função SQL se disponível
    try {
      const { data: userExists, error: checkError } = await supabase
        .rpc('check_user_exists', { user_email: normalizedEmail })

      if (!checkError && userExists) {
        return { exists: true }
      }
    } catch {
      // RPC indisponível — tenta fallback abaixo
    }

    // Fallback: RPC pode falhar ou ser case-sensitive; confirma via get_existing_user_info
    const existing = await getExistingUserInfo(normalizedEmail)
    if (existing?.id) {
      return { exists: true, userInfo: existing }
    }

    return { exists: false }
  } catch (error) {
    return {
      exists: false,
      error: 'Erro inesperado ao verificar usuário'
    }
  }
}

/**
 * Validação completa do formulário de criação de usuário
 */
export const validateUserForm = (formData: {
  name: string
  email: string
  password: string
  confirmPassword: string
  user_group_id: string
  helpdesk_user_group_id?: string
}): ValidationResult => {
  // Validar nome
  const nameValidation = validateName(formData.name)
  if (!nameValidation.isValid) {
    return nameValidation
  }

  // Validar email
  const emailValidation = validateEmailFormat(formData.email)
  if (!emailValidation.isValid) {
    return emailValidation
  }

  // Validar senha
  const passwordValidation = validatePassword(formData.password, formData.confirmPassword)
  if (!passwordValidation.isValid) {
    return passwordValidation
  }

  // Validar: pelo menos um grupo (QG ou Portal de Atendimento)
  if (!formData.user_group_id && !formData.helpdesk_user_group_id) {
    return {
      isValid: false,
      error: 'Informe o Grupo de Permissões ou o Grupo (Portal Atendimento)'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Busca dados do usuário em auth.users pelo e-mail (RPC segura).
 */
export const getExistingUserInfo = async (email: string): Promise<ExistingUserInfo | null> => {
  try {
    const { data, error } = await supabase.rpc('get_existing_user_info', {
      user_email: normalizeEmail(email)
    })

    if (error || !data || typeof data !== 'object' || !('id' in data)) {
      return null
    }

    return data as ExistingUserInfo
  } catch {
    return null
  }
}

/**
 * Interpreta a resposta do supabase.auth.signUp.
 * Trata user null (anti-enumeration), identities vazias (e-mail duplicado)
 * e recupera userId via RPC quando o Auth criou o usuário mas não retornou user.
 */
export const resolveSignUpResult = async (
  data: SignUpResponseData | null,
  error: { message: string; code?: string } | null,
  email: string,
  messages?: {
    success?: string
    duplicate?: string
    failure?: string
  }
): Promise<SignUpResult> => {
  const trimmedEmail = normalizeEmail(email)
  const successMsg =
    messages?.success ??
    'Usuário registrado com sucesso! Aguarde a ativação por um administrador.'
  const duplicateMsg =
    messages?.duplicate ??
    'Este e-mail já está cadastrado. Use outro e-mail ou recupere sua senha.'
  const failureMsg =
    messages?.failure ??
    'Não foi possível concluir o cadastro. Tente novamente.'

  if (error) {
    return { success: false, message: handleAuthError(error) }
  }

  const user = data?.user ?? data?.session?.user ?? null

  if (user) {
    const identities = user.identities ?? []
    if (identities.length === 0) {
      return { success: false, message: duplicateMsg, isDuplicate: true }
    }
    return { success: true, message: successMsg, userId: user.id }
  }

  const existing = await getExistingUserInfo(trimmedEmail)
  if (existing?.id) {
    // Auth às vezes não devolve user quando o e-mail já existe (anti-enumeration).
    // Se já há perfil/confirmação, trate como duplicado — não como cadastro novo.
    if (existing.email_confirmed_at || existing.profile_name != null || existing.profile_active != null) {
      return { success: false, message: duplicateMsg, isDuplicate: true }
    }
    return { success: true, message: successMsg, userId: existing.id }
  }

  return { success: false, message: failureMsg }
}

/**
 * Trata erros específicos do Supabase Auth
 */
export const handleAuthError = (error: any): string => {
  if (error.message.includes('Unable to validate email address')) {
    return 'Erro ao criar usuário: E-mail inválido. Tente novamente.'
  } else if (error.message.includes('User already registered') || 
             error.message.includes('already been registered') ||
             error.message.includes('already exists') ||
             error.message.includes('duplicate') ||
             error.code === '23505') {
    return 'Erro ao criar usuário: Este e-mail já está cadastrado. Use outro e-mail.'
  } else if (error.message.includes('Password should be at least')) {
    return 'Erro ao criar usuário: A senha deve ter pelo menos 6 caracteres.'
  } else if (error.message.includes('Invalid login credentials')) {
    return 'Erro ao criar usuário: E-mail ou senha inválidos.'
  } else if (error.message.includes('Signup is disabled')) {
    return 'Erro ao criar usuário: Cadastro de novos usuários está desabilitado.'
  } else {
    return `Erro ao criar usuário: ${error.message}`
  }
}

/**
 * Trata erros específicos do banco de dados (perfil)
 */
export const handleProfileError = (error: any): string => {
  if (error.message.includes('duplicate key') || error.code === '23505') {
    return 'Erro ao criar usuário: Este usuário já existe no sistema. Tente com outro e-mail.'
  } else if (error.message.includes('foreign key') || error.code === '23503') {
    return 'Erro ao criar usuário: Grupo de usuário inválido.'
  } else if (error.message.includes('permission denied') || error.code === '42501') {
    return 'Erro ao criar usuário: Sem permissão para criar usuários.'
  } else if (error.message.includes('unique constraint')) {
    return 'Erro ao criar usuário: Este e-mail já está em uso. Use outro e-mail.'
  } else {
    return `Erro ao criar usuário: ${error.message}`
  }
}
